import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "api/axios.config";
import {
  constructApiQuery,
  getMyMattersList as getMyMattersListAPI,
  getMatterTasks as getMatterTasksAPI,
  getMattersdetails as getMatterDetailsAPI,
  getMatterAllInfo as getMatterAllInfoAPI,
} from "api/index";

const MATTERS_BASE = "/Matters";

const getMattersListAPI = (
  page,
  limit,
  orderBy,
  filterParameters,
  status,
) => {
  let url = `${MATTERS_BASE}?`;
  if (page) {
    url += `&page=${page}`;
  }
  if (limit) {
    url += `&limit=${limit}`;
  }
  if (orderBy) {
    url += `&orderby=${orderBy}`;
  }
  if (status) {
    url += `&status=${status}`;
  }

  const parameters = constructApiQuery(
    filterParameters?.branch || null,
    filterParameters?.teamId || null,
    filterParameters?.feeEarnerRef || null,
  );

  if (Object.keys(parameters).length) {
    url += `&${Object.keys(parameters)[0]}=${Object.values(parameters)[0]}`;
  }
  if (filterParameters.openFrom) {
    url += `&OpenStartDate=${filterParameters.openFrom}`;
  }
  if (filterParameters.openTo) {
    url += `&OpenEndDate=${filterParameters.openTo}`;
  }
  if (filterParameters.closeFrom) {
    url += `&CloseStartDate=${filterParameters.closeFrom}`;
  }
  if (filterParameters.closeTo) {
    url += `&CloseEndDate=${filterParameters.closeTo}`;
  }
  if (filterParameters?.appCode) {
    url += `&AppCode=${filterParameters.appCode}`;
  }
  if (filterParameters?.departmentId) {
    url += `&DepartmentId=${filterParameters.departmentId}`;
  }
  if (filterParameters?.workTypeId) {
    url += `&WorkTypeId=${filterParameters.workTypeId}`;
  }
  if (filterParameters.search) {
    url += `&search=${filterParameters.search}`;
  }
  return API.get(url);
};

const getMatterActivitiesAPI = (
  id,
  feeEarner,
  FromDate,
  ToDate,
  typeId,
  page,
  limit,
  parameter,
  search,
  tagIds,
) => {
  let url = `/MatterActivities/${id}?active=true&page=${page}&limit=${limit}`;

  if (Array.isArray(typeId)) {
    typeId.forEach((id) => {
      if (id !== "" && id != null) url += `&typeId=${id}`;
    });
  } else if (typeId) {
    url += `&typeId=${typeId}`;
  }
  if (feeEarner) {
    feeEarner?.map((item) => {
      url += `&FeeEarnerRef=${item}`;
    });
  }
  if (FromDate) {
    url += `&FromDate=${FromDate}`;
  }
  if (ToDate) {
    url += `&ToDate=${ToDate}`;
  }
  if (parameter) {
    url += `&orderby=${parameter}, activityId desc`;
  }
  if (search) {
    url += `&search=${search}`;
  }
  if (tagIds?.length) {
    tagIds.forEach((id) => { url += `&TagIds=${id}`; });
  }
  return API.get(url);
};

const getWorkDoneActivitiesAPI = ({
  branchId,
  teamId,
  feeEarnerRef,
  FromDate,
  ToDate,
  typeId,
  page,
  limit,
  parameter,
  search,
  tagIds,
  matterId,
  clientId,
  activityFeeEarnerRefs,
}) => {
  const params = new URLSearchParams({
    active: "true",
    page: String(page),
    limit: String(limit),
  });

  const apiQuery = constructApiQuery(branchId, teamId, feeEarnerRef);
  Object.entries(apiQuery).forEach(([key, value]) => params.append(key, value));
  (activityFeeEarnerRefs || []).forEach((ref) =>
    params.append("ActivityFeeEarnerRefs", ref),
  );

  (Array.isArray(typeId) ? typeId : [typeId])
    .filter((id) => id !== "" && id != null)
    .forEach((id) => params.append("typeId", id));
  (tagIds || []).forEach((id) => params.append("TagIds", id));
  if (matterId) params.append("MatterId", matterId);
  if (clientId) params.append("ClientId", clientId);
  if (FromDate) params.append("FromDate", FromDate);
  if (ToDate) params.append("ToDate", ToDate);
  params.append(
    "orderby",
    parameter ? `${parameter}, activityId desc` : "date desc, activityId desc",
  );
  if (search) params.append("search", search);

  const url = `/MatterActivities/workdone?${params.toString()}`;
  return API.get(url);
};


const getCanClose = async (matterId) => {
  const response = await API.get(`${MATTERS_BASE}/${matterId}/CanClose`);
  const payload = response.data?.data ?? {};
  return {
    allowed: Boolean(payload.canClose),
    reasons: (payload.reasons ?? []).filter(Boolean),
  };
};

const closeMatter = (matterId, data) =>
  API.patch(`${MATTERS_BASE}/${matterId}/Close`, JSON.stringify({ data }), {
    headers: { "Content-Type": "application/json" },
  });

const reopenMatter = (matterId, authCode) =>
  API.patch(
    `${MATTERS_BASE}/${matterId}/Reopen?authcode=${encodeURIComponent(authCode)}`,
    null,
    { headers: { "Content-Type": "application/json" } },
  );

const getPaymentProfile = (matterId) =>
  API.get(`${MATTERS_BASE}/${matterId}/PaymentProfile`);

const updatePaymentProfile = (matterId, paymentProfile) =>
  API.put(
    `${MATTERS_BASE}/${matterId}/PaymentProfile`,
    JSON.stringify({ data: paymentProfile }),
    { headers: { "Content-Type": "application/json-patch+json" } },
  );

const buildOrderBy = (sort, fallback = "matterRef") => {
  if (!sort) return fallback;
  return sort.dir === "desc" ? `${sort.field} desc` : sort.field;
};

const fetchMattersList = createAsyncThunk(
  "matters/fetchList",
  async ({ page, limit, sort, filterParameters, status, searchValue }) => {
    const params = {
      ...(filterParameters || {}),
      ...(searchValue ? { search: searchValue } : {}),
    };
    const res = await getMattersListAPI(
      page,
      limit,
      buildOrderBy(sort),
      params,
      status,
    );
    return res.data;
  },
);

const fetchMyMattersList = createAsyncThunk(
  "matters/fetchMyList",
  async ({ page, limit, sort, filterParameters }) => {
    const res = await getMyMattersListAPI(
      page,
      limit,
      buildOrderBy(sort),
      filterParameters || {},
    );
    return res.data;
  },
);

const fetchMatterActivities = createAsyncThunk(
  "matters/fetchMatterActivities",
  async ({
    matterId,
    feeEarner,
    fromDate,
    toDate,
    typeId,
    page,
    limit,
    sort,
    searchValue,
    tagIds,
  }) => {
    const res = await getMatterActivitiesAPI(
      matterId,
      feeEarner,
      fromDate,
      toDate,
      typeId,
      page,
      limit,
      buildOrderBy(sort, "typeCode"),
      searchValue,
      tagIds,
    );
    return { matterId, data: res.data };
  },
);

const fetchMatterTasks = createAsyncThunk(
  "matters/fetchMatterTasks",
  async ({ matterId, completed, fromDate, toDate, page, limit, sort }) => {
    const res = await getMatterTasksAPI(
      matterId,
      completed,
      fromDate,
      toDate,
      page,
      limit,
      buildOrderBy(sort, "date"),
    );
    return { matterId, data: res.data };
  },
);

const fetchWorkDoneActivities = createAsyncThunk(
  "matters/fetchWorkDoneActivities",
  async ({ sort, searchValue, ...rest }) => {
    const res = await getWorkDoneActivitiesAPI({
      ...rest,
      parameter: buildOrderBy(sort, undefined),
      search: searchValue,
    });
    return res.data;
  },
);

const fetchMatterDetails = createAsyncThunk(
  "matters/fetchMatterDetails",
  async (matterId) => {
    const res = await getMatterDetailsAPI(matterId);
    return res.data;
  },
);

const fetchMatterAllInfo = createAsyncThunk(
  "matters/fetchMatterAllInfo",
  async (matterId) => {
    const res = await getMatterAllInfoAPI(matterId);
    return res.data;
  },
);

const initialState = {
  isLoading: undefined,
  matterViewDetails: {},
  matterActivities: {},
  matterTasks: {},
  workDoneActivities: { data: [], total: 0 },
};

const mattersSlice = createSlice({
  name: "mattersList",
  initialState,
  reducers: {
    clearMyMattersList: (state) => {
      state.isLoading = false;
      state.myMattersPageList = undefined;
    },
    setMatterAllInfo: (state, action) => {
      state.isLoading = false;
      const matter = action.payload;
      state.matterViewDetails[matter.matterRef] = { data: matter };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMattersList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMattersList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.mattersList = action.payload;
      })
      .addCase(fetchMattersList.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchMyMattersList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMyMattersList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.myMattersPageList = action.payload;
      })
      .addCase(fetchMyMattersList.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchMatterDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMatterDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.mattersDetails = action.payload;
      })
      .addCase(fetchMatterDetails.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchMatterAllInfo.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMatterAllInfo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.matterViewDetails[action.payload.data.matterRef] = action.payload;
      })
      .addCase(fetchMatterAllInfo.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchMatterActivities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMatterActivities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.matterActivities[action.payload.matterId] = action.payload.data;
      })
      .addCase(fetchMatterActivities.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchMatterTasks.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMatterTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.matterTasks[action.payload.matterId] = action.payload.data;
      })
      .addCase(fetchMatterTasks.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchWorkDoneActivities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchWorkDoneActivities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workDoneActivities = action.payload;
      })
      .addCase(fetchWorkDoneActivities.rejected, (state) => {
        state.isLoading = false;
        state.workDoneActivities = { data: [], total: 0 };
      });
  },
});

const mattersReducer = mattersSlice.reducer;
const { clearMyMattersList, setMatterAllInfo } = mattersSlice.actions;

export {
  mattersReducer,
  fetchMattersList,
  fetchMyMattersList,
  fetchMatterActivities,
  fetchMatterTasks,
  fetchWorkDoneActivities,
  fetchMatterDetails,
  fetchMatterAllInfo,
  clearMyMattersList,
  setMatterAllInfo,
  getCanClose,
  closeMatter,
  reopenMatter,
  getPaymentProfile,
  updatePaymentProfile,
};
